import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, TrendingUp, DollarSign, AlertTriangle, BarChart3 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Super Admin uses a dedicated token stored in localStorage.superAdminToken
// (not the global axios defaults set by AuthContext for student/teacher/HT). Read it per call.
const saAuth = () => {
  const t = typeof window !== 'undefined' ? localStorage.getItem('superAdminToken') : null;
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

const featureLabels = {
  skill_feedback: 'Skill Assessment Feedback',
  ai_action_plan: 'AI Action Plan',
  spoken_english_stt: 'Spoken English (STT)',
  spoken_english_tts: 'Spoken English (TTS)',
  audio_learning_tts: 'Audio Learning (TTS)',
  mock_grading: 'Mock Exam Descriptive Grading',
  question_generation: 'Question Generation',
  image_generation: 'Image Generation',
};

const featureColor = (f) => {
  if (f === 'spoken_english_stt') return 'bg-red-100 text-red-700 border-red-200';
  if (f === 'audio_learning_tts' || f === 'spoken_english_tts') return 'bg-orange-100 text-orange-700 border-orange-200';
  return 'bg-blue-100 text-blue-700 border-blue-200';
};

const AIUsageDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ym, setYm] = useState(() => new Date().toISOString().slice(0, 7));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        axios.get(`${API}/super-admin/ai-usage/summary`, { params: { ym }, ...saAuth() }),
        axios.get(`${API}/super-admin/ai-usage/trend`, { params: { months: 6 }, ...saAuth() }),
      ]);
      setSummary(s.data);
      setTrend(t.data || []);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [ym]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin text-indigo-500" /></div>;
  }
  if (!summary) return null;

  const totalUsd = summary.total_cost_usd || 0;
  const totalInr = (totalUsd * 84).toFixed(0);

  // Generate last 6 months options
  const monthOptions = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    monthOptions.push(d.toISOString().slice(0, 7));
  }

  return (
    <div className="space-y-6" data-testid="ai-usage-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white rounded-xl p-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <DollarSign size={22} /> AI Usage &amp; Cost
          </h2>
          <p className="text-indigo-100 text-sm">Track 3rd-party AI spend across all schools for the month.</p>
        </div>
        <select
          value={ym}
          onChange={e => setYm(e.target.value)}
          className="bg-white/10 border border-white/30 rounded-lg px-3 py-2 text-sm text-white"
          data-testid="ai-usage-month-select"
        >
          {monthOptions.map(m => <option key={m} value={m} className="text-gray-900">{m}</option>)}
        </select>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-xl p-5">
          <p className="text-emerald-100 text-xs uppercase">Total spend ({ym})</p>
          <p className="text-3xl font-bold">${totalUsd.toFixed(2)}</p>
          <p className="text-sm opacity-90">≈ ₹{totalInr}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-5">
          <p className="text-blue-100 text-xs uppercase">Schools active this month</p>
          <p className="text-3xl font-bold">{summary.by_school.length}</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-xl p-5">
          <p className="text-amber-100 text-xs uppercase">Features used</p>
          <p className="text-3xl font-bold">{summary.by_feature.length}</p>
        </div>
      </div>

      {/* By feature */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="bg-gray-50 px-5 py-3 border-b">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2"><BarChart3 size={18} /> Spend by feature</h3>
        </div>
        {summary.by_feature.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No AI calls logged this month.</div>
        ) : (
          <div className="divide-y">
            {summary.by_feature.map(f => {
              const pct = totalUsd > 0 ? (f.cost_usd / totalUsd * 100).toFixed(1) : 0;
              return (
                <div key={f.feature} className="px-5 py-3" data-testid={`feature-row-${f.feature}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${featureColor(f.feature)}`}>
                        {featureLabels[f.feature] || f.feature}
                      </span>
                      <span className="text-xs text-gray-500">{f.calls.toLocaleString()} calls</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-800">${f.cost_usd.toFixed(4)}</p>
                      <p className="text-[10px] text-gray-500">{pct}% of total</p>
                    </div>
                  </div>
                  <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-indigo-400 to-purple-500" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* By school */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="bg-gray-50 px-5 py-3 border-b">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2"><TrendingUp size={18} /> Top spending schools</h3>
        </div>
        {summary.by_school.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No school activity this month.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-5 py-2">School</th>
                <th className="text-right px-5 py-2">Calls</th>
                <th className="text-right px-5 py-2">Cost (USD)</th>
                <th className="text-right px-5 py-2">Cost (INR)</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {summary.by_school.map(s => (
                <tr key={s.school_id} className="hover:bg-gray-50" data-testid={`school-row-${s.school_id}`}>
                  <td className="px-5 py-2.5 font-medium text-gray-800">{s.school_name}</td>
                  <td className="px-5 py-2.5 text-right text-gray-700">{s.calls.toLocaleString()}</td>
                  <td className="px-5 py-2.5 text-right font-semibold">${s.cost_usd.toFixed(4)}</td>
                  <td className="px-5 py-2.5 text-right text-gray-500">₹{(s.cost_usd * 84).toFixed(0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Trend */}
      {trend.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><TrendingUp size={18} /> 6-month trend</h3>
          <div className="flex items-end gap-2 h-32">
            {trend.map(t => {
              const max = Math.max(...trend.map(x => x.cost_usd), 0.01);
              const h = Math.max(4, (t.cost_usd / max) * 100);
              return (
                <div key={t.ym} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[10px] font-semibold text-gray-700">${t.cost_usd.toFixed(2)}</span>
                  <div
                    className={`w-full rounded-t bg-gradient-to-t from-indigo-500 to-indigo-300`}
                    style={{ height: `${h}%` }}
                  />
                  <span className="text-[10px] text-gray-500">{t.ym.slice(5)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Hint */}
      <div className="bg-blue-50 border-l-4 border-blue-400 rounded-lg p-3 flex items-start gap-2">
        <AlertTriangle size={16} className="text-blue-600 mt-0.5 shrink-0" />
        <p className="text-xs text-blue-800">
          Spoken English (STT) is typically the biggest line item. Head Teachers can cap daily/monthly minutes per student under their Settings → Usage Caps.
        </p>
      </div>
    </div>
  );
};

export default AIUsageDashboard;
